import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  INCIDENT_TYPE_LABELS,
  INCIDENT_STATUS_LABELS,
  INCIDENT_AMV_HINT,
} from "@/lib/basProcess/constants";
import { DocumentHeader } from "@/components/BasProcess/DocumentHeader";
import { generateIncidentReportWordDocument } from "@/lib/documentGenerator/incidentReportWordGenerator";
import { OfflineBanner } from "@/components/BasProcess/OfflineBanner";
import {
  cacheGet,
  cacheSet,
  newLocalId,
  offlineDelete,
  offlineInsert,
  offlineUpdate,
} from "@/lib/offline/queue";
import { AlertTriangle, Plus, Trash2, FileDown, UserPlus, X } from "lucide-react";
import type { IncidentReport, IncidentPerson, IncidentType, IncidentStatus } from "@/types/incidentReport";
import { AiPolishField } from "@/components/common/AiPolishField";

interface Props {
  projectId: string;
}

const SELECT_COLUMNS = "id, project_id, incident_type, occurred_at, location, description, involved_persons, witnesses, immediate_actions, root_cause, consequences, reported_to_amv, amv_report_date, amv_case_number, status, assigned_to, closed_at, closed_by, closure_note, requires_amp_revision, amp_revision_note, risk_assessment_id, new_risk_id, stage_id, created_by, updated_by, created_at, updated_at";

export const IncidentReports: React.FC<Props> = ({ projectId }) => {
  const [reports, setReports] = useState<IncidentReport[]>([]);
  const [stages, setStages] = useState<{ id: string; name: string }[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const navigate = useNavigate();

  const cacheKey = `incident_reports:${projectId}`;

  const load = async () => {
    const { data, error } = await supabase
      .from("incident_reports")
      .select(SELECT_COLUMNS)
      .eq("project_id", projectId)
      .order("occurred_at", { ascending: false });
    if (error) {
      const cached = cacheGet<IncidentReport[]>(cacheKey);
      if (cached) {
        setReports(cached);
        toast.info("Visar sparad kopia (offline)");
      } else {
        toast.error("Kunde inte läsa tillbud/olyckor");
      }
    } else {
      const list = (data || []) as unknown as IncidentReport[];
      setReports(list);
      cacheSet(cacheKey, list);
    }
    const { data: stageData } = await supabase
      .from("project_stages")
      .select("id, name")
      .eq("project_id", projectId)
      .order("sort_order");
    setStages((stageData as { id: string; name: string }[] | null) ?? []);
    setLoading(false);
  };

  useEffect(() => {
    setLoading(true);
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  const create = async () => {
    const row = {
      id: newLocalId(),
      project_id: projectId,
      incident_type: "tillbud",
      occurred_at: new Date().toISOString(),
      location: "",
      description: "",
      involved_persons: [],
      witnesses: [],
      status: "draft",
    };
    const res = await offlineInsert("incident_reports", row);
    setReports((prev) => {
      const next = [row as unknown as IncidentReport, ...prev];
      cacheSet(cacheKey, next);
      return next;
    });
    setExpandedId(row.id);
    if (res.queued) toast.success("Rapport skapad offline – synkas när nätet är tillbaka");
  };

  const update = async (id: string, patch: Partial<IncidentReport>) => {
    setReports((prev) => {
      const next = prev.map((r) => (r.id === id ? { ...r, ...patch } : r));
      cacheSet(cacheKey, next);
      return next;
    });
    await offlineUpdate("incident_reports", id, patch as Record<string, unknown>);
  };

  const remove = async (id: string) => {
    setReports((prev) => {
      const next = prev.filter((r) => r.id !== id);
      cacheSet(cacheKey, next);
      return next;
    });
    await offlineDelete("incident_reports", id);
  };


  const openRiskAssessment = (report: IncidentReport) => {
    if (report.risk_assessment_id) {
      navigate(`/riskbedomning?assessmentId=${report.risk_assessment_id}`);
    }
  };

  if (loading) return <div className="py-12 text-center text-muted-foreground">Laddar tillbud och olyckor...</div>;

  const openCount = reports.filter((r) => r.status !== "closed").length;
  const seriousCount = reports.filter((r) => ["allvarligt_tillbud", "allvarlig_olycka"].includes(r.incident_type)).length;

  return (
    <div className="space-y-4">
      <OfflineBanner onSynced={load} />
      <DocumentHeader
        docNumber="28"
        exportHistory={{ documentType: "tillbud" }}
        title="Tillbud- och olycksrapportering"
        description="Rapportering och utredning enligt AFS 2023:1. Allvarliga händelser ska anmälas till Arbetsmiljöverket."
        icon={AlertTriangle}
        meta={
          <div className="flex items-center gap-2">
            {seriousCount > 0 && <Badge variant="destructive">{seriousCount} allvarliga</Badge>}
            <Badge variant={openCount > 0 ? "destructive" : "outline"}>{openCount} öppna</Badge>
          </div>
        }
        actions={
          <Button onClick={create}>
            <Plus className="h-4 w-4 mr-1" />Ny rapport
          </Button>
        }
      />

      {reports.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center space-y-3">
            <AlertTriangle className="h-8 w-8 mx-auto text-muted-foreground" />
            <p className="text-sm text-muted-foreground">Inga rapporterade tillbud eller olyckor ännu.</p>
            <Button onClick={create}>Rapportera första händelsen</Button>
          </CardContent>
        </Card>
      ) : (
        reports.map((report) => (
          <Card key={report.id} className={report.status === "closed" ? "opacity-90" : ""}>
            <CardHeader className="pb-3">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  <CardTitle className="text-base">
                    {INCIDENT_TYPE_LABELS[report.incident_type]} – {report.location || "Okänd plats"}
                  </CardTitle>
                  <Badge variant={report.status === "closed" ? "outline" : "destructive"}>
                    {INCIDENT_STATUS_LABELS[report.status]}
                  </Badge>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => generateIncidentReportWordDocument(report)}
                  >
                    <FileDown className="h-4 w-4 mr-1" />Word
                  </Button>
                  <Button variant="ghost" size="icon" onClick={() => remove(report.id)}>
                    <Trash2 className="h-4 w-4 text-muted-foreground" />
                  </Button>
                </div>
              </div>
              <p className="text-xs text-muted-foreground">
                {new Date(report.occurred_at).toLocaleString("sv-SE")}
                {report.assigned_to && <> · Ansvarig: {report.assigned_to}</>}
              </p>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label>Typ av händelse</Label>
                  <Select
                    value={report.incident_type}
                    onValueChange={(v) => update(report.id, { incident_type: v as IncidentType })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {(Object.keys(INCIDENT_TYPE_LABELS) as IncidentType[]).map((t) => (
                        <SelectItem key={t} value={t}>{INCIDENT_TYPE_LABELS[t]}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Status</Label>
                  <Select
                    value={report.status}
                    onValueChange={(v) => {
                      const status = v as IncidentStatus;
                      const patch: Partial<IncidentReport> = { status };
                      if (status === "closed") {
                        patch.closed_at = new Date().toISOString();
                      } else {
                        patch.closed_at = undefined;
                      }
                      update(report.id, patch);
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {(Object.keys(INCIDENT_STATUS_LABELS) as IncidentStatus[]).map((s) => (
                        <SelectItem key={s} value={s}>{INCIDENT_STATUS_LABELS[s]}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Tidpunkt</Label>
                  <Input
                    type="datetime-local"
                    value={report.occurred_at.slice(0, 16)}
                    onChange={(e) => update(report.id, { occurred_at: new Date(e.target.value).toISOString() })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Plats</Label>
                  <Input
                    value={report.location}
                    onChange={(e) => update(report.id, { location: e.target.value })}
                  />
                </div>
                {stages.length > 0 && (
                  <div className="space-y-2">
                    <Label>Etapp</Label>
                    <Select
                      value={(report as { stage_id?: string | null }).stage_id ?? "none"}
                      onValueChange={(v) =>
                        update(report.id, {
                          stage_id: v === "none" ? null : v,
                        } as Partial<IncidentReport>)
                      }
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Välj etapp" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Hela projektet</SelectItem>
                        {stages.map((s) => (
                          <SelectItem key={s.id} value={s.id}>
                            {s.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <Label>Beskrivning av händelsen</Label>
                <AiPolishField
                  rows={3}
                  value={report.description}
                  onChange={(v) => update(report.id, { description: v })}
                  context="Beskrivning av tillbud eller olycka på byggarbetsplats"
                  placeholder="Beskriv vad som hände, orsak och förlopp..."
                />
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <PersonList
                  title="Inblandade personer"
                  persons={report.involved_persons || []}
                  onChange={(persons) => update(report.id, { involved_persons: persons })}
                />
                <PersonList
                  title="Vittnen"
                  persons={report.witnesses || []}
                  onChange={(persons) => update(report.id, { witnesses: persons })}
                />
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label>Omedelbara åtgärder</Label>
                  <Textarea
                    rows={2}
                    value={report.immediate_actions || ""}
                    onChange={(e) => update(report.id, { immediate_actions: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Rotorsak</Label>
                  <AiPolishField
                    rows={2}
                    value={report.root_cause || ""}
                    onChange={(v) => update(report.id, { root_cause: v })}
                    context="Bakomliggande orsak till tillbud eller olycka"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Konsekvenser</Label>
                  <Textarea
                    rows={2}
                    value={report.consequences || ""}
                    onChange={(e) => update(report.id, { consequences: e.target.value })}
                  />
                </div>
                <div className="space-y-2 sm:col-span-2 rounded-md border border-amber-500/40 bg-amber-500/5 p-3">
                  <label className="flex items-center gap-2 text-sm font-medium">
                    <input
                      type="checkbox"
                      className="h-4 w-4 accent-primary"
                      checked={Boolean(report.requires_amp_revision)}
                      onChange={(e) =>
                        update(report.id, { requires_amp_revision: e.target.checked })
                      }
                    />
                    Tillbudet innebär att arbetsmiljöplanen behöver revideras
                  </label>
                  {report.requires_amp_revision && (
                    <Textarea
                      rows={2}
                      placeholder="Vad i arbetsmiljöplanen behöver ändras?"
                      value={report.amp_revision_note || ""}
                      onChange={(e) => update(report.id, { amp_revision_note: e.target.value })}
                    />
                  )}
                  <p className="text-xs text-muted-foreground">
                    Flaggan syns i Bas-P-inkorgen och under Frister tills tillbudet är avslutat.
                  </p>
                </div>
                <div className="space-y-2">
                  <Label>Åtgärdsansvarig</Label>
                  <Input
                    value={report.assigned_to || ""}
                    onChange={(e) => update(report.id, { assigned_to: e.target.value })}
                    placeholder="Namn och roll"
                  />
                </div>
              </div>

              <Card className="bg-muted/30 border-border/50">
                <CardContent className="py-4 space-y-3">
                  <div className="flex items-center gap-2">
                    <input
                      id={`amv-${report.id}`}
                      type="checkbox"
                      className="h-4 w-4 rounded border-border"
                      checked={report.reported_to_amv}
                      onChange={(e) => update(report.id, { reported_to_amv: e.target.checked })}
                    />
                    <Label htmlFor={`amv-${report.id}`} className="font-medium">
                      Anmäld till Arbetsmiljöverket
                    </Label>
                  </div>
                  <p className="text-xs text-muted-foreground">{INCIDENT_AMV_HINT}</p>
                  {report.reported_to_amv && (
                    <div className="grid gap-3 sm:grid-cols-2">
                      <div className="space-y-1">
                        <Label className="text-xs">Anmälningsdatum</Label>
                        <Input
                          type="date"
                          value={report.amv_report_date || ""}
                          onChange={(e) => update(report.id, { amv_report_date: e.target.value })}
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Diarienummer</Label>
                        <Input
                          value={report.amv_case_number || ""}
                          onChange={(e) => update(report.id, { amv_case_number: e.target.value })}
                        />
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              {report.status === "closed" && (
                <div className="space-y-2">
                  <Label>Avslutningsnotering</Label>
                  <AiPolishField
                    rows={2}
                    value={report.closure_note || ""}
                    onChange={(v) => update(report.id, { closure_note: v })}
                    context="Avslutsnotering för tillbuds-/olycksärende"
                    placeholder="Sammanfatta hur ärendet avslutades..."
                  />
                </div>
              )}

              <div className="flex flex-wrap gap-2 pt-2">
                {report.risk_assessment_id ? (
                  <Button variant="outline" size="sm" onClick={() => openRiskAssessment(report)}>
                    Öppna kopplad risk
                  </Button>
                ) : (
                  <Button variant="outline" size="sm" onClick={() => navigate(`/riskbedomning?projectId=${projectId}&incidentId=${report.id}`)}>
                    Skapa risk från händelsen
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        ))
      )}
    </div>
  );
};

interface PersonListProps {
  title: string;
  persons: IncidentPerson[];
  onChange: (persons: IncidentPerson[]) => void;
}

const PersonList: React.FC<PersonListProps> = ({ title, persons, onChange }) => {
  const add = () => onChange([...persons, { name: "" }]);
  const updatePerson = (idx: number, patch: Partial<IncidentPerson>) => {
    const next = persons.map((p, i) => (i === idx ? { ...p, ...patch } : p));
    onChange(next);
  };
  const removePerson = (idx: number) => {
    onChange(persons.filter((_, i) => i !== idx));
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <Label>{title}</Label>
        <Button variant="ghost" size="sm" onClick={add}>
          <UserPlus className="h-4 w-4 mr-1" />Lägg till
        </Button>
      </div>
      {persons.length === 0 && <p className="text-xs text-muted-foreground">Inga personer angivna.</p>}
      {persons.map((p, i) => (
        <div key={i} className="flex flex-wrap gap-2 items-center">
          <Input
            placeholder="Namn"
            value={p.name}
            onChange={(e) => updatePerson(i, { name: e.target.value })}
            className="flex-1 min-w-full sm:w-[120px]"
          />
          <Input
            placeholder="Företag"
            value={p.company || ""}
            onChange={(e) => updatePerson(i, { company: e.target.value })}
            className="w-full sm:w-[140px]"
          />
          <Input
            placeholder="Roll"
            value={p.role || ""}
            onChange={(e) => updatePerson(i, { role: e.target.value })}
            className="w-full sm:w-[120px]"
          />
          <div className="flex items-center gap-2">
            <input
              id={`injured-${i}`}
              type="checkbox"
              className="h-4 w-4 rounded border-border"
              checked={!!p.injured}
              onChange={(e) => updatePerson(i, { injured: e.target.checked })}
            />
            <Label htmlFor={`injured-${i}`} className="text-xs">Skadad</Label>
          </div>
          <Button variant="ghost" size="icon" onClick={() => removePerson(i)}>
            <X className="h-4 w-4 text-muted-foreground" />
          </Button>
        </div>
      ))}
    </div>
  );
};

export default IncidentReports;
